import {
  getUsers,
  orEmptyArray,
  type SitePermissions,
  type UserResponse,
} from '@lms/api-client/backend';
import { Card, CardContent, CardDescription, CardHeader } from '@lms/ui/card';
import { Checkbox } from '@lms/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { useToast } from '@lms/ui/use-toast';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import { PageBox, PageHeader, PageTitle } from '@/components/admin/page-box';
import { AccessMatrixSection } from '@/features/access-matrix/access-matrix-section';
import { useCurrentUser, useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { NotFound } from '@/routes/not-found';
import AddUser from './add-user';
import OverflowMenu from './overflow-menu';
import { setUserPermissions, usersKey } from './users-api';

// One user's permission checkboxes at the current site. Super-admins are
// shown as fully permitted and can't be edited here. A user can't take away
// their own Manage users permission, since that would lock them out of this
// page.
function PermissionCells({
  user,
  siteId,
  isSelf,
}: {
  user: UserResponse;
  siteId: number;
  isSelf: boolean;
}) {
  const { mutate } = useSWRConfig();
  const { toast } = useToast();
  const [pending, setPending] = useState(false);

  const here = user.roles.find((r) => r.siteId === siteId);
  if (!here) {
    return (
      <>
        <TableCell />
        <TableCell />
      </>
    );
  }

  const superAdmin = here.role === 'super-admin';
  const current: SitePermissions = {
    canManageGroups: superAdmin || here.canManageGroups,
    canManageUsers: superAdmin || here.canManageUsers,
  };

  const update = async (change: Partial<SitePermissions>) => {
    setPending(true);
    try {
      await setUserPermissions(user.id, siteId, { ...current, ...change });
      await mutate(usersKey(siteId));
    } catch (e) {
      toast({ variant: 'destructive', description: `${e}` });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <TableCell>
        <Checkbox
          checked={current.canManageGroups}
          disabled={pending || superAdmin}
          onCheckedChange={(c) => update({ canManageGroups: c === true })}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={current.canManageUsers}
          disabled={pending || superAdmin || isSelf}
          onCheckedChange={(c) => update({ canManageUsers: c === true })}
        />
      </TableCell>
    </>
  );
}

// Ports app/[brand]/admin/users/page.tsx: on a location, the users table plus
// the add-user card; on a portfolio, the user × location access matrix. The Next page loaded users on the server; the SPA loads them with SWR
// (keyed so mutations elsewhere in the feature revalidate this list). Managing
// users needs `canManageUsers`, so the page is denied to viewers without it.
export function UsersSection() {
  const t = useTranslations();
  const { brand: site, slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const { user: me } = useCurrentUser();
  // The list is only shown on locations; portfolios get the access matrix.
  const { data: users, isLoading } = useSWR(
    site.siteType === 'portfolio' ? null : usersKey(site.id),
    () => orEmptyArray(getUsers(site.id)),
  );

  if (roleLoading) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <LoaderCircle className="text-primary size-12 animate-spin" />
      </div>
    );
  }

  if (!access?.canManageUsers) {
    return <NotFound />;
  }

  // On a customer the access matrix is the whole page: adding and removing
  // people, customer-wide permissions and per-location access all live there.
  if (site.siteType === 'portfolio') {
    return (
      <PageBox>
        <PageHeader>
          <PageTitle>{t('admin.users')}</PageTitle>
        </PageHeader>
        <AccessMatrixSection portfolioId={site.id} customerName={site.name} />
      </PageBox>
    );
  }

  return (
    <PageBox>
      <PageHeader>
        <PageTitle>{t('admin.users')}</PageTitle>
      </PageHeader>
      <div className="grid items-start gap-8 md:grid-cols-4">
        <Card className="col-span-1 md:col-span-3">
          <CardHeader>
            <CardDescription>
              {t('admin.manage', {
                what: t('admin.users').toLowerCase(),
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.name')}</TableHead>
                  <TableHead>{t('admin.email')}</TableHead>
                  <TableHead>{t('permissions.manageGroups')}</TableHead>
                  <TableHead>{t('permissions.manageUsers')}</TableHead>
                  <TableHead className="w-5"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <LoaderCircle className="text-primary size-5 animate-spin" />
                    </TableCell>
                  </TableRow>
                ) : (
                  (users ?? []).map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.email}</TableCell>
                      <PermissionCells
                        user={p}
                        siteId={site.id}
                        isSelf={p.id === me?.id}
                      />
                      <TableCell className="text-right">
                        <OverflowMenu id={p.id} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <AddUser />
      </div>
    </PageBox>
  );
}
